import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import ExtractorInput from './ExtractorInput';
import FingerExtractor from './FingerExtractor';
import IrisExtractor from './IrisExtractor';
import MatcherInput from './MatcherInput';
import FingerMatcher from './FingerMatcher';
import IrisMatcher from './IrisMatcher';
import QualityChecker from './QualityChecker';
import FingerQuality from './FingerQuality';
import IrisQuality from './IrisQuality';
import { Eye, Fingerprint, User, Scan, GitCompare, Gauge } from 'lucide-react';
import {
  ISO_NAMESPACE_ISO,
  ISO_NAMESPACE_MOSIP,
  getStoredIsoNamespace,
  setStoredIsoNamespace,
} from './biosdkRoutes';
import './App.css';

function ContainerNav() {
  const location = useLocation();
  const currentPath = location.pathname;

  let links = [];
  
  if (currentPath.startsWith('/extractor')) {
    links = [
      { to: '/extractor', text: 'Face', icon: User },
      { to: '/extractor/finger', text: 'Finger', icon: Fingerprint },
      { to: '/extractor/iris', text: 'Iris', icon: Eye }
    ];
  } else if (currentPath.startsWith('/matcher')) {
    links = [
      { to: '/matcher', text: 'Face', icon: User },
      { to: '/matcher/finger', text: 'Finger', icon: Fingerprint },
      { to: '/matcher/iris', text: 'Iris', icon: Eye }
    ];
  } else if (currentPath.startsWith('/quality')) {
    links = [
      { to: '/quality', text: 'Face', icon: User },
      { to: '/quality/finger', text: 'Finger', icon: Fingerprint },
      { to: '/quality/iris', text: 'Iris', icon: Eye }
    ];
  }

  return (
    <div className="container-nav">
      {links.map(link => {
        const Icon = link.icon;
        const isSelected = currentPath === link.to;
        return (
          <Link
            key={link.to}
            to={link.to}
            className={`nav-tab ${isSelected ? 'selected' : ''}`}
          >
            <Icon className="nav-icon" size={18} />
            <span>{link.text}</span>
          </Link>
        );
      })}
    </div>
  );
}

function DefaultMessage() {
  const navigate = useNavigate();

  const handleFeatureClick = (path) => {
    navigate(path);
  };

  return (
    <div className="default-message">
      <div className="welcome-card">
        <img src="/logo.png" alt="Ooru BioCHQ" className="welcome-logo" />
        <h2>Welcome to Ooru BioCHQ</h2>
        <p>Select a category to get started.</p>
        <div className="feature-grid">
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/extractor')}>
            <Scan className="feature-icon" />
            <h3>Extractor</h3>
            <p>Extract biometric templates from images</p>
          </div>
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/matcher')}>
            <GitCompare className="feature-icon" />
            <h3>Matcher</h3>
            <p>Compare biometric samples for verification</p>
          </div>
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/quality')}>
            <Gauge className="feature-icon" />
            <h3>Quality</h3>
            <p>Analyze biometric image quality</p>
          </div>
        </div>
        <div className="modality-badges">
          <span className="modality-badge face">
            <User size={14} /> Face
          </span>
          <span className="modality-badge finger">
            <Fingerprint size={14} /> Finger
          </span>
          <span className="modality-badge iris">
            <Eye size={14} /> Iris
          </span>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [isoNamespace, setIsoNamespace] = useState(getStoredIsoNamespace());
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  const getNavClass = (path) => {
    if (currentPath.startsWith(path)) return 'nav-link active';
    return 'nav-link';
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleIsoNamespaceChange = (event) => {
    const selected = setStoredIsoNamespace(event.target.value);
    setIsoNamespace(selected);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div className="logo-section" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Ooru BioCHQ" className="logo-icon" />
            <h1 className="service-name">Ooru BioCHQ</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="iso-namespace-select" style={{ fontSize: 12, opacity: 0.9 }}>ISO route</label>
            <select
              id="iso-namespace-select"
              value={isoNamespace}
              onChange={handleIsoNamespaceChange}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cfd7e3' }}
            >
              <option value={ISO_NAMESPACE_MOSIP}>mosip</option>
              <option value={ISO_NAMESPACE_ISO}>iso</option>
            </select>
          </div>
          <span className="version-badge">v1.0</span>
        </div>
      </header>
    
      <nav className="main-nav">
        <div className="nav-links">
          <Link to="/extractor" className={getNavClass('/extractor')}>
            <Scan size={18} />
            <span>Extractor</span>
          </Link>
          <Link to="/matcher" className={getNavClass('/matcher')}>
            <GitCompare size={18} />
            <span>Matcher</span>
          </Link>
          <Link to="/quality" className={getNavClass('/quality')}>
            <Gauge size={18} />
            <span>Quality</span>
          </Link>
        </div>
      </nav>

      <div className="main-container">
        <ContainerNav />

        <Routes>
          {/* Default Route */}
          <Route path="/" element={<DefaultMessage />} />
          
          {/* Extractor Routes */}
          <Route path="/extractor" element={<ExtractorInput />} />
          <Route path="/extractor/finger" element={<FingerExtractor />} />
          <Route path="/extractor/iris" element={<IrisExtractor />} />
          
          {/* Matcher Routes */}
          <Route path="/matcher" element={<MatcherInput />} />
          <Route path="/matcher/finger" element={<FingerMatcher />} />
          <Route path="/matcher/iris" element={<IrisMatcher />} />

          {/* Quality Routes */}
          <Route path="/quality" element={<QualityChecker />} />
          <Route path="/quality/finger" element={<FingerQuality />} />
          <Route path="/quality/iris" element={<IrisQuality />} />

          {/* Fallback Route */}
          <Route path="*" element={<DefaultMessage />} />
        </Routes>
      </div>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;
